import cloneDeep from 'lodash/cloneDeep';
import AlignIcon from '../../../app/components/svgIcons/AlignIcon';
import CallIcon from '../../../app/components/svgIcons/CallIcon';
import EditIcon from '../../../app/components/svgIcons/EditIcon';
import FileAttachmentIcon from '../../../app/components/svgIcons/FileAttachmentIcon';
import FileFormIcon from '../../../app/components/svgIcons/FileFormIcon';
import FormDateButtonIcon from '../../../app/components/svgIcons/FormDateButtonIcon';
import FormEmailIcon from '../../../app/components/svgIcons/FormEmailIcon';
import FormRadioButtonIcon from '../../../app/components/svgIcons/FormRadioButtonIcon';
import GlobeIcon from '../../../app/components/svgIcons/GlobeIcon';
import LongTextIcon from '../../../app/components/svgIcons/LongTextIcon';
import MultipleChoiceIcon from '../../../app/components/svgIcons/MultipleChoiceIcon';
import SortingIcon from '../../../app/components/svgIcons/SortingIcon';
import StarIcon from '../../../app/components/svgIcons/StarIcon';
import TextIcon from '../../../app/components/svgIcons/TextIcon';
import BagIcon from '../../../app/components/svgIcons/Bagicon';
import CustomizeFormViewIcon from '../../../app/components/svgIcons/CustomizeFormViewIcon';
import VideoRecordingOutlineIcon from '../../../app/components/svgIcons/VideoRecordingOutlineIcon';
import AnalyticsIcon from '../../../app/components/svgIcons/AnalyticsIcon';
import OutlineEyeIcon from '../../../app/components/svgIcons/OutlineEyeIcon';
import LocationIcon from '../../../app/components/svgIcons/LocationIcon';
import PinnedIcon from '../../../app/components/svgIcons/PinnedIcon';
import GlobeUnFilledIcon from '../../../app/components/svgIcons/GlobeUnFilledIcon';
import ShieldIcon from '../../../app/components/svgIcons/ShieldIcon';
import CheckListIcon from '../../../app/components/svgIcons/CheckListIcon';
import BriefCaseIcon from '../../../app/components/svgIcons/BriefCaseIcon';
import TextTicon from '../../../app/components/svgIcons/TextTicon';
import MultiLinesIcon from '../../../app/components/svgIcons/MultiLinesIcon';
import { ControlTypes, uuidv4 } from '../../../utils/helper';

// const REFERENCE_FORM_TYPE = 8;

/** Backend OCCY-6038: these types are not validated by OccyForms. */
const OCCY_FORMS_UNSUPPORTED = new Set([
  ControlTypes.YES_NO,
  ControlTypes.MULTI_FILE,
  ControlTypes.RANKING,
  ControlTypes.MATRIX,
  ControlTypes.VIDEO_ANSWER,
  ControlTypes.CALCULATED,
  ControlTypes.HIDDEN_FIELD,
  ControlTypes.ADDRESS,
  ControlTypes.POSTCODE,
  ControlTypes.CONSENT,
  ControlTypes.RIGHT_TO_WORK,
  ControlTypes.EMPLOYMENT_HISTORY,
]);

export const QUESTION_CATEGORIES = [
  { key: 'basic', label: 'Basic' },
  { key: 'choice', label: 'Choice' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'compliance', label: 'Compliance & location' },
  { key: 'layout', label: 'Layout' },
];

export const RIGHT_TO_WORK_OPTIONS = [
  { label: 'I have the right to work with no restrictions' },
  { label: 'I have the right to work with restrictions' },
  { label: 'I need sponsorship' },
  { label: 'Prefer not to say' },
];

const defaultChoiceOptions = (allowMultiple = true) => ({
  allowMultiple,
  opts: [{ label: undefined }],
});

const HELP_TEXT = ['helpText'];
const HELP_PLACEHOLDER = ['helpText', 'placeholder'];
const HELP_OPTIONS = ['helpText', 'options'];

export const MAX_FILE_SIZE_MB = 25;

const fileSettings = (placeholder = '.pdf, .doc, .docx', extras = []) => [
  {
    key: 'allowedTypes',
    label: 'Allowed file types',
    type: 'text',
    placeholder,
  },
  ...extras,
  {
    key: 'maxSizeMb',
    label: 'Maximum size (MB)',
    type: 'number',
    min: 1,
    max: MAX_FILE_SIZE_MB,
  },
];

export const QUESTION_TYPE_REGISTRY = [
  {
    key: 'short_answer',
    sectionLabel: 'Short Answer',
    badgeLabel: 'short answer',
    description: 'One line of text',
    category: 'basic',
    sectionType: ControlTypes.TEXT_INPUT,
    icon: TextIcon,
    allowOnReference: true,
    content: HELP_PLACEHOLDER,
    settings: [
      { key: 'maxLength', label: 'Character limit', type: 'number', min: 1 },
    ],
  },
  {
    key: 'long_answer',
    sectionLabel: 'Long Answer',
    badgeLabel: 'long answer',
    description: 'Longer, multi-line answer',
    category: 'basic',
    sectionType: ControlTypes.TEXTAREA,
    icon: LongTextIcon,
    allowOnReference: true,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'number',
    sectionLabel: 'Number',
    badgeLabel: 'number',
    description: 'Numbers only',
    category: 'basic',
    sectionType: ControlTypes.NUMBER_INPUT,
    icon: TextIcon,
    allowOnReference: false,
    content: HELP_PLACEHOLDER,
    settings: [
      { key: 'min', label: 'Minimum value', type: 'number' },
      { key: 'max', label: 'Maximum value', type: 'number' },
    ],
  },
  {
    key: 'email',
    sectionLabel: 'Email',
    badgeLabel: 'email',
    description: 'Email address',
    category: 'basic',
    sectionType: ControlTypes.EMAIL,
    icon: FormEmailIcon,
    allowOnReference: false,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'phone',
    sectionLabel: 'Phone',
    badgeLabel: 'phone',
    description: 'Phone number',
    category: 'basic',
    sectionType: ControlTypes.PHONE,
    icon: CallIcon,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'url',
    sectionLabel: 'Website / link',
    badgeLabel: 'link',
    description: 'Website or profile link',
    category: 'basic',
    sectionType: ControlTypes.URL,
    icon: GlobeIcon,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'date',
    sectionLabel: 'Date',
    badgeLabel: 'date',
    description: 'Pick a single date',
    category: 'basic',
    sectionType: ControlTypes.DATE,
    icon: FormDateButtonIcon,
    allowOnReference: true,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'availability_range',
    sectionLabel: 'Availability range',
    badgeLabel: 'availability',
    description: 'Pick a start and end date',
    category: 'basic',
    sectionType: ControlTypes.AVAILABILITY_RANGE,
    icon: FormDateButtonIcon,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'file_upload',
    sectionLabel: 'File upload',
    badgeLabel: 'file upload',
    description: 'One file, such as a CV',
    category: 'basic',
    sectionType: ControlTypes.FILE_UPLOAD,
    icon: FileFormIcon,
    content: HELP_TEXT,
    settings: fileSettings(),
  },
  {
    key: 'multiple_files',
    sectionLabel: 'Multiple files',
    badgeLabel: 'multiple files',
    description: 'Several files in one answer',
    category: 'basic',
    sectionType: ControlTypes.MULTI_FILE,
    icon: FileAttachmentIcon,
    sectionOptions: { multiple: true },
    content: HELP_TEXT,
    settings: fileSettings('.pdf, .doc, .docx', [
      { key: 'maxFiles', label: 'Maximum files', type: 'number', min: 1 },
    ]),
  },
  {
    key: 'multiple_choice',
    sectionLabel: 'Multiple choice',
    badgeLabel: 'multiple choice',
    description: 'Pick one or more options',
    category: 'choice',
    sectionType: ControlTypes.CHECKBOX,
    icon: MultipleChoiceIcon,
    sectionOptions: defaultChoiceOptions(true),
    hasOptions: true,
    allowOnReference: true,
    content: HELP_OPTIONS,
  },
  {
    key: 'checkboxes',
    sectionLabel: 'Checkboxes',
    badgeLabel: 'checkboxes',
    description: 'Tick all that apply',
    category: 'choice',
    sectionType: ControlTypes.CHECKBOX,
    icon: MultipleChoiceIcon,
    sectionOptions: defaultChoiceOptions(true),
    hasOptions: true,
    content: HELP_OPTIONS,
  },
  {
    key: 'radio',
    sectionLabel: 'Radio buttons',
    badgeLabel: 'radio buttons',
    description: 'Pick exactly one option',
    category: 'choice',
    sectionType: ControlTypes.RADIO,
    icon: FormRadioButtonIcon,
    sectionOptions: defaultChoiceOptions(false),
    hasOptions: true,
    allowOnReference: true,
    content: HELP_OPTIONS,
  },
  {
    key: 'dropdown',
    sectionLabel: 'Dropdown',
    badgeLabel: 'dropdown',
    description: 'Pick one from a list',
    category: 'choice',
    sectionType: ControlTypes.DROPDOWN,
    icon: AlignIcon,
    sectionOptions: { opts: [{ label: undefined }] },
    hasOptions: true,
    allowOnReference: true,
    content: ['helpText', 'placeholder', 'options'],
  },
  {
    key: 'yes_no',
    sectionLabel: 'Yes / No',
    badgeLabel: 'yes no',
    description: 'A simple yes or no',
    category: 'choice',
    sectionType: ControlTypes.YES_NO,
    icon: MultipleChoiceIcon,
    sectionOptions: {
      allowMultiple: true,
      opts: [{ label: 'Yes' }, { label: 'No' }],
    },
    hasOptions: true,
    allowOnReference: false,
    content: HELP_OPTIONS,
  },
  {
    key: 'ranking',
    sectionLabel: 'Ranking',
    badgeLabel: 'ranking',
    description: 'Put the options in order',
    category: 'choice',
    sectionType: ControlTypes.RANKING,
    icon: SortingIcon,
    sectionOptions: defaultChoiceOptions(true),
    hasOptions: true,
    content: HELP_OPTIONS,
  },
  {
    key: 'rating',
    sectionLabel: 'Rating',
    badgeLabel: 'rating',
    description: 'Star rating',
    category: 'advanced',
    sectionType: ControlTypes.RATING,
    icon: StarIcon,
    sectionOptions: { max: 5 },
    content: HELP_TEXT,
    settings: [
      { key: 'max', label: 'Maximum rating', type: 'number', min: 1, max: 10 },
    ],
  },
  {
    key: 'slider',
    sectionLabel: 'Slider / scale',
    badgeLabel: 'slider',
    description: 'Choose a value on a scale',
    category: 'advanced',
    sectionType: ControlTypes.SLIDER,
    icon: AlignIcon,
    sectionOptions: { min: 0, max: 10, step: 1 },
    content: HELP_TEXT,
    settings: [
      { key: 'min', label: 'Minimum', type: 'number' },
      { key: 'max', label: 'Maximum', type: 'number' },
      { key: 'step', label: 'Step', type: 'number' },
      { key: 'minLabel', label: 'Label for lowest value', type: 'text' },
      { key: 'maxLabel', label: 'Label for highest value', type: 'text' },
    ],
  },
  {
    key: 'matrix',
    sectionLabel: 'Matrix / grid',
    badgeLabel: 'matrix',
    description: 'Rate several rows on one scale',
    category: 'advanced',
    sectionType: ControlTypes.MATRIX,
    icon: CustomizeFormViewIcon,
    sectionOptions: {
      opts: [{ label: undefined }, { label: undefined }],
      rows: [{ label: undefined }, { label: undefined }],
    },
    hasOptions: true,
    optionsLabel: 'Columns',
    content: HELP_OPTIONS,
    settings: [{ key: 'rows', label: 'Rows', type: 'rows' }],
  },
  {
    key: 'salary',
    sectionLabel: 'Salary expectation',
    badgeLabel: 'salary',
    description: 'Expected salary amount',
    category: 'advanced',
    sectionType: ControlTypes.SALARY,
    icon: BagIcon,
    content: HELP_PLACEHOLDER,
    settings: [
      { key: 'min', label: 'Minimum amount', type: 'number', min: 0 },
      { key: 'max', label: 'Maximum amount', type: 'number', min: 0 },
    ],
  },
  {
    key: 'signature',
    sectionLabel: 'Signature',
    badgeLabel: 'signature',
    description: 'Draw signature on a pad',
    category: 'advanced',
    sectionType: ControlTypes.SIGNATURE,
    icon: EditIcon,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'video_answer',
    sectionLabel: 'Video answer',
    badgeLabel: 'video answer',
    description: 'Upload a short video reply',
    category: 'advanced',
    sectionType: ControlTypes.VIDEO_ANSWER,
    icon: VideoRecordingOutlineIcon,
    sectionOptions: {
      allowedTypes: '.mp4, .mov, .webm',
      maxSizeMb: MAX_FILE_SIZE_MB,
    },
    content: HELP_TEXT,
    settings: fileSettings('.mp4, .mov, .webm', [
      {
        key: 'guidance',
        label: 'On-screen guidance',
        type: 'text',
        placeholder: 'Keep it under 2 minutes',
      },
    ]),
  },
  {
    key: 'calculated',
    sectionLabel: 'Calculated field',
    badgeLabel: 'calculated',
    description: 'Worked out from earlier answers',
    category: 'advanced',
    sectionType: ControlTypes.CALCULATED,
    icon: AnalyticsIcon,
    neverRequired: true,
    sectionOptions: { decimals: 0 },
    content: HELP_TEXT,
    settings: [
      {
        key: 'formula',
        label: 'Formula',
        type: 'text',
        placeholder: '{Q1} * {Q2} / 100',
      },
      { key: 'decimals', label: 'Decimal places', type: 'number', min: 0, max: 6 },
      { key: 'prefix', label: 'Prefix', type: 'text' },
      { key: 'suffix', label: 'Suffix', type: 'text' },
    ],
  },
  {
    key: 'hidden_field',
    sectionLabel: 'Hidden field',
    badgeLabel: 'hidden',
    description: 'Captured silently, never shown',
    category: 'advanced',
    sectionType: ControlTypes.HIDDEN_FIELD,
    icon: OutlineEyeIcon,
    isHiddenField: true,
    content: [],
    settings: [
      {
        key: 'queryParam',
        label: 'Read from URL parameter',
        type: 'text',
        placeholder: 'utm_source',
      },
      { key: 'defaultValue', label: 'Fallback value', type: 'text' },
    ],
  },
  {
    key: 'address',
    sectionLabel: 'Address',
    badgeLabel: 'address',
    description: 'Full address with lookup',
    category: 'compliance',
    sectionType: ControlTypes.ADDRESS,
    icon: LocationIcon,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'postcode',
    sectionLabel: 'Postcode',
    badgeLabel: 'postcode',
    description: 'Postcode or ZIP code',
    category: 'compliance',
    sectionType: ControlTypes.POSTCODE,
    icon: PinnedIcon,
    content: HELP_PLACEHOLDER,
    settings: [
      { key: 'maxLength', label: 'Character limit', type: 'number', min: 1 },
    ],
  },
  {
    key: 'country',
    sectionLabel: 'Country',
    badgeLabel: 'country',
    description: 'Pick a country from the list',
    category: 'compliance',
    sectionType: ControlTypes.COUNTRY,
    icon: GlobeUnFilledIcon,
    content: HELP_PLACEHOLDER,
  },
  {
    key: 'consent_checkbox',
    sectionLabel: 'Consent checkbox',
    badgeLabel: 'consent',
    description: 'A single statement to agree to',
    category: 'compliance',
    sectionType: ControlTypes.CONSENT,
    icon: ShieldIcon,
    sectionOptions: {
      consentText: 'I agree to the terms described above.',
    },
    content: HELP_TEXT,
    settings: [
      {
        key: 'consentText',
        label: 'Statement to agree to',
        type: 'text',
        placeholder: 'I agree to the terms described above.',
      },
      { key: 'policyUrl', label: 'Policy link (optional)', type: 'text' },
    ],
  },
  {
    key: 'right_to_work',
    sectionLabel: 'Right to work',
    badgeLabel: 'right to work',
    description: 'Work eligibility declaration',
    category: 'compliance',
    sectionType: ControlTypes.RIGHT_TO_WORK,
    icon: CheckListIcon,
    sectionOptions: { opts: [...RIGHT_TO_WORK_OPTIONS] },
    hasOptions: true,
    optionsLabel: 'Statuses',
    content: HELP_OPTIONS,
    settings: [
      {
        key: 'evidenceLabel',
        label: 'Evidence upload label',
        type: 'text',
        placeholder: 'Leave empty to skip the upload',
      },
    ],
  },
  {
    key: 'employment_history',
    sectionLabel: 'Employment history',
    badgeLabel: 'employment history',
    description: 'Repeating list of previous roles',
    category: 'compliance',
    sectionType: ControlTypes.EMPLOYMENT_HISTORY,
    icon: BriefCaseIcon,
    sectionOptions: { maxEntries: 5 },
    content: HELP_TEXT,
    settings: [
      { key: 'minEntries', label: 'Minimum roles', type: 'number', min: 1 },
      { key: 'maxEntries', label: 'Maximum roles', type: 'number', min: 1 },
      {
        key: 'entryLabel',
        label: 'Label for each entry',
        type: 'text',
        placeholder: 'Role',
      },
    ],
  },
  {
    key: 'heading',
    sectionLabel: 'Heading',
    badgeLabel: 'heading',
    description: 'Section heading text',
    category: 'layout',
    sectionType: ControlTypes.HEADING,
    icon: TextTicon,
    isDisplayOnly: true,
    content: [],
    labelField: {
      label: 'Heading',
      placeholder: 'Section heading',
    },
    defaultSectionLabel: 'Section heading',
  },
  {
    key: 'paragraph',
    sectionLabel: 'Paragraph',
    badgeLabel: 'paragraph',
    description: 'Block of explanatory text',
    category: 'layout',
    sectionType: ControlTypes.PARAGRAPH,
    icon: AlignIcon,
    isDisplayOnly: true,
    content: ['content'],
    bodyLabel: 'Body text',
    labelField: {
      label: 'Label (not shown)',
      placeholder: 'Paragraph',
    },
    defaultSectionLabel: 'Paragraph',
    sectionOptions: { content: 'Add your text here.' },
  },
  {
    key: 'divider',
    sectionLabel: 'Divider',
    badgeLabel: 'divider',
    description: 'Horizontal line between fields',
    category: 'layout',
    sectionType: ControlTypes.DIVIDER,
    icon: MultiLinesIcon,
    isDisplayOnly: true,
    content: [],
    hideSectionLabel: true,
    emptyContentMessage:
      'A divider just draws a line — nothing to configure.',
    defaultSectionLabel: '',
  },
  {
    key: 'statement',
    sectionLabel: 'Statement',
    badgeLabel: 'statement',
    description: 'Read-only text, no answer needed',
    category: 'layout',
    sectionType: ControlTypes.STATEMENT,
    icon: TextTicon,
    isDisplayOnly: true,
    hideFromPalette: true,
    content: ['body'],
    bodyLabel: 'Statement text',
  },
  {
    key: 'section_break',
    sectionLabel: 'Section break',
    badgeLabel: 'section break',
    description: 'Splits a long page into parts',
    category: 'layout',
    sectionType: ControlTypes.SECTION_BREAK,
    icon: MultiLinesIcon,
    isDisplayOnly: true,
    hideFromPalette: true,
    content: ['body'],
    bodyLabel: 'Description (optional)',
  },
];

export const getQuestionTypeConfig = (questionOrType) => {
  if (!questionOrType) return null;
  if (typeof questionOrType === 'string') {
    return (
      QUESTION_TYPE_REGISTRY.find((item) => item.key === questionOrType) ||
      QUESTION_TYPE_REGISTRY.find((item) => item.sectionType === questionOrType)
    );
  }
  return (
    QUESTION_TYPE_REGISTRY.find((item) => item.key === questionOrType.typeKey) ||
    QUESTION_TYPE_REGISTRY.find((item) => item.key === questionOrType.sectionOptions?.typeKey) ||
    QUESTION_TYPE_REGISTRY.find(
      (item) => item.sectionType === questionOrType.sectionType,
    )
  );
};

export const isDisplayOnlyControl = (questionOrType) =>
  !!getQuestionTypeConfig(questionOrType)?.isDisplayOnly;

export const isHiddenFieldControl = (questionOrType) =>
  !!getQuestionTypeConfig(questionOrType)?.isHiddenField;

export const questionRequiresLabel = (questionOrType) =>
  !getQuestionTypeConfig(questionOrType)?.hideSectionLabel;

/** Controls that only present text, so they are never numbered or validated. */
export const isNonAnswerableControl = (questionOrType) => {
  const sectionType =
    typeof questionOrType === 'string'
      ? questionOrType
      : questionOrType?.sectionType;
  return (
    sectionType === ControlTypes.SECTION || isDisplayOnlyControl(questionOrType)
  );
};

export const getQuestionContentFields = (questionOrType) => {
  const config = getQuestionTypeConfig(questionOrType);
  if (config?.content) return config.content;
  return config?.hasOptions ? ['helpText', 'options'] : ['helpText'];
};

export const getBuilderQuestionTypes = (formType) => {
  const supported = QUESTION_TYPE_REGISTRY.filter(
    (item) =>
      !OCCY_FORMS_UNSUPPORTED.has(item.sectionType) && !item.hideFromPalette,
  );
  // if (formType === REFERENCE_FORM_TYPE) {
  //   return supported.filter((item) => item.allowOnReference);
  // }
  return supported;
};

export const getQuestionTypesByCategory = (formType) => {
  const types = getBuilderQuestionTypes(formType);
  return QUESTION_CATEGORIES.map((category) => ({
    ...category,
    types: types.filter((item) => item.category === category.key),
  })).filter((category) => category.types.length > 0);
};

export const createTempClientId = () => `tmp-${uuidv4()}`;

export const createQuestionFromType = (typeConfig, extras = {}) => {
  if (!typeConfig) return null;
  return {
    clientId: createTempClientId(),
    sectionLabel:
      typeConfig.defaultSectionLabel !== undefined
        ? typeConfig.defaultSectionLabel
        : '',
    sectionType: typeConfig.sectionType,
    typeKey: typeConfig.key,
    icon: typeConfig.icon,
    ...(typeConfig.sectionOptions
      ? { sectionOptions: cloneDeep(typeConfig.sectionOptions) }
      : {}),
    active: true,
    ...extras,
  };
};
